import React, { FC, useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import QuestionCard from './QuestionCard'
import { questionnaireData } from '../../data/questionnaireData'
import { useRiskQuestionnaireStore } from '../../state-management/questionnaireStore'
import PrimaryButton from '../ui-components/PrimaryButton'
import BackButton from '../ui-components/BackButton'
import { getBottomPadding, getTopPadding } from '../../utils/sessionStorage'
import { disableHardwareBack, goBack, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
// 1. Import motion and AnimatePresence from Framer Motion
import { motion, AnimatePresence } from 'framer-motion'

// 2. Define the animation variants for the question card
const questionVariants = {
  // State when question is entering
  enter: {
    x: '100%',
    opacity: 0
  },
  // State when question is in the center (visible)
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  // State when question is exiting
  exit: {
    zIndex: 0,
    x: '-100%',
    opacity: 0
  }
}

const RiskQuestionnaire = () => {
  const ScreenName = 'RISK_QUESTIONNAIRE'
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')

  const navigate = useNavigate()
  const {
    currentQuestionIndex,
    answers,
    setCurrentQuestionIndex,
    addAnswer,
    calculateRiskProfile,
    resetQuestionnaire
  } = useRiskQuestionnaireStore()
  const [loading, setLoading] = useState(false)

  const currentQuestion = useMemo(() => questionnaireData[currentQuestionIndex], [currentQuestionIndex])

  // The rest of your hooks and logic (useMemo, useCallback, etc.) remain exactly the same.
  // ...
  const selectedAnswer = useMemo(
    () => answers.find((a) => a.questionNumber === currentQuestion?.id),
    [answers, currentQuestion?.id]
  )

  const selectedOptionId = useMemo(
    () =>
      selectedAnswer
        ? currentQuestion?.options.find((opt) => opt.riskPoints === selectedAnswer.riskPoints)?.id || null
        : null,
    [selectedAnswer, currentQuestion?.options]
  )

  const handleOptionSelect = useCallback(
    (questionNumber: number, riskPoints: number) => {
      const currentQuestionData = questionnaireData.find((q) => q.id === questionNumber)
      const selectedOption = currentQuestionData?.options.find((opt) => opt.riskPoints === riskPoints)

      if (currentQuestionData && selectedOption) {
        addAnswer({
          questionNumber,
          riskPoints,
          optionId: selectedOption.id,
          optionText: selectedOption.text,
          questionText: currentQuestionData.questionText
        })
      }

      if (questionNumber > 1) {
        if (questionNumber < questionnaireData.length) {
          setCurrentQuestionIndex(questionNumber)
        } else {
          setLoading(true)
          setTimeout(() => {
            calculateRiskProfile()
            setLoading(false)
            if (from === 'rebalance-plan') {
              navigate('/risk-profile-result?from=rebalance-plan')
            } else {
              navigate('/risk-profile-result')
            }
          }, 500)
        }
      }
    },
    [addAnswer, setCurrentQuestionIndex, calculateRiskProfile, navigate]
  )

  const handleConfirm = () => {
    if (currentQuestionIndex < questionnaireData.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handleBack = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: { eventId: 'Back_Button_Risk_Questionnaire', ScreenName }
    })
    if (from === 'APP') goBack()
    else if (from === 'rebalance-plan') navigate('/rebalance-plan?from=first-time-risk-assessment')
    else navigate(-1)
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName }
    })
    updateAppStatusBar('FFFFFF', 'dark')
    disableHardwareBack()
    resetQuestionnaire()
  }, [])

  const isOptionSelected = useMemo(
    () => answers.some((a) => a.questionNumber === currentQuestion.id),
    [answers, currentQuestion.id]
  )

  if (!currentQuestion) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p>No questions found. Please check your data.</p>
      </div>
    )
  }

  return (
    <>
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 10px)` }}
        className='flex w-max items-center justify-center rounded-[50%] animate-fade-150 pl-6 pb-5'
      >
        <BackButton fill={'#000000'} size={24} onClick={handleBack} />
      </div>
      {/* 3. Wrap the changing content in a container with a fixed height and overflow-hidden */}
      <div className='bg-white h-[calc(100vh-84px)] mt-4 flex items-center justify-center overflow-scroll no-scrollbar'>
        <div className='w-full h-full max-w-2xl flex flex-col justify-between mt-4'>
          {/* 4. Use AnimatePresence to manage the enter/exit animations */}
          <AnimatePresence mode='wait'>
            {/* 5. Wrap the QuestionCard in a motion.div and apply the animation props */}
            <motion.div
              key={currentQuestion.id}
              variants={questionVariants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{
                x: { type: 'spring', stiffness: 400, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className='h-full'
            >
              <QuestionCard
                question={currentQuestion}
                onSelect={handleOptionSelect}
                selectedOptionId={selectedOptionId || null}
              />
            </motion.div>
          </AnimatePresence>

          {currentQuestion.id === 1 && (
            <div
              style={{ bottom: `calc(${getBottomPadding()}px + 16px)` }}
              className='mt-8 px-5 absolute left-0 right-0 w-full z-10'
            >
              <PrimaryButton
                label='Confirm'
                isFullWidth
                onClick={handleConfirm}
                disabled={!isOptionSelected}
                labelClassName=''
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default RiskQuestionnaire
